import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { recipesByCompanyID } from "graphql/queries";
import { updateRecipe } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import { getCompanyName } from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import RecipeCreateForm from "./RecipeCreateForm";
import RecipeUpdateForm from "./RecipeUpdateForm";

const Recipes = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "agro:read",
    "agro:read",
    "agro:read",
  ]);
  const [recipes, setRecipes] = useState([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [recipeDetails, setRecipeDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    recipeId: null,
    recipeName: null,
  });
  const [modalRecipe, setModalRecipe] = useState({
    visible: false,
    recipeId: null,
    action: null
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      recipes.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        date: item.date ? moment(item.date).format("YYYY-MM-DD") : "-",
        actions: (
          <>
            <ActionIcon
              name={
                recipeDetails?.id === item.id
                  ? strings.recipes.hide
                  : strings.recipes.show
              }
              icon={
                recipeDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                recipeDetails?.id === item.id
                  ? setRecipeDetails(null)
                  : setRecipeDetails(item);
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.recipes.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <ActionIcon
                name={strings.recipes.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    recipeId: item.id,
                    recipeName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recipes, recipeDetails?.id]
  );

  const onNext = () => {
    fetchRecipes(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchRecipes(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchRecipes(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalRecipe({ visible: true, action, recipeId: id });
  const onSuccessRecipe = () => {
    setModalRecipe({ visible: false });
    fetchRecipes();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByRecipes = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setRecipes((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        if (index > -1) {
          updatedList[index] = {
            ...updatedList[index],
            companyName,
          };
        }

        return updatedList;
      });
    });
  };

  async function fetchRecipes(token, isNext = true, keywords) {
    const nameOperation = recipesByCompanyID;
    const nameOperationStr = "recipesByCompanyID";
    try {
      setRecipesLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      const recipesData = await API.graphql(
        graphqlOperation(nameOperation, {
          companyID: searchBy.company.id || me.companyID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
              and: keywords.map((key) => ({
                or: [
                  {
                    name: { contains: key },
                  },
                ],
              })),
              deletedAt: { attributeExists: false },
            }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const recipesItems = recipesData.data[nameOperationStr].items;
      setRecipes(recipesItems);
      getNamesByRecipes(recipesItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: recipesData.data[nameOperationStr].nextToken,
        };
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      console.error(err);
    } finally {
      setRecipesLoading(false);
    }
  }
  async function recipeDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateRecipe, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateRecipe.id;
      setRecipes(recipes.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        recipeId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchRecipes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id, searchBy.company.id]);

  const tableColumns = [
    {
      key: "code",
      label: strings.table.headers.code,
      _style: { width: "15%" },
    },
    {
      key: "date",
      label: strings.table.headers.date,
      _style: { width: "15%" },
    },
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "20%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "20%" },
    },
    {
      key: "status",
      label: strings.table.headers.status,
      _style: { width: "15%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "15%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.recipes.addRecipe}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {recipesLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!recipeDetails?.id} style={styles.collapseCard}>
          <CCard key={recipeDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{recipeDetails?.name}"
              </h4>

              <p className="text-muted">
                <b>{strings.details.code}:</b> {recipeDetails?.code ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.date}:</b>{" "}
                {recipeDetails?.date
                  ? moment(recipeDetails.date).format("YYYY-MM-DD")
                  : "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.type}:</b> {recipeDetails?.type ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.name}:</b> {recipeDetails?.name ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.recipeDetails}:</b>{" "}
                {recipeDetails?.recipeDetails ?? "-"}
              </p>

              <hr />

              <p className="text-muted">
                <b>{strings.details.inputId}:</b>{" "}
                {recipeDetails?.inputId ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.availableInventoryQuantity}:</b>{" "}
                {recipeDetails?.availableInventoryQuantity ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.inventoryUnit}:</b>{" "}
                {recipeDetails?.inventoryUnit ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.inputQuantityToUse}:</b>{" "}
                {recipeDetails?.inputQuantityToUse ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.applicationUnit}:</b>{" "}
                {recipeDetails?.applicationUnit ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.preparationProcedure}:</b>{" "}
                {recipeDetails?.preparationProcedure ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.status}:</b> {recipeDetails?.status ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {recipeDetails?.createdAt
                  ? moment(recipeDetails.createdAt).format(
                    "YYYY-MM-DD HH:mm:ss"
                  )
                  : "-"}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.recipeName,
        })}
        onCancel={() => setModalDetails({ visible: false, recipeId: null })}
        onConfirm={() => recipeDelete(modalDetails.recipeId)}
      />
      {modalRecipe.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalRecipe.visible}
          onClose={() => setModalRecipe({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalRecipe.action === "create" && strings.modalRecipe.addTitle}
              {modalRecipe.action === "update" && strings.modalRecipe.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalRecipe.action === "create" && (
                <RecipeCreateForm onSuccess={onSuccessRecipe} />
              )}
              {modalRecipe.action === "update" && (
                <RecipeUpdateForm
                  recipeId={modalRecipe.recipeId}
                  onSuccess={onSuccessRecipe}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Recetas",
    successDeleted: "Receta eliminada con éxito",
    recipes: {
      addRecipe: "Agregar Receta",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar las recetas",
    },
    table: {
      headers: {
        code: "Código",
        date: "Fecha",
        name: "Nombre",
        companyName: "Empresa",
        status: "Estado",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles de la Receta",
      code: "Código",
      date: "Fecha",
      type: "Tipo",
      name: "Nombre",
      recipeDetails: "Detalle receta",
      inputId: "Insumo (ID)",
      availableInventoryQuantity: "Cantidad disponible",
      inventoryUnit: "Unidad inventario",
      inputQuantityToUse: "Cantidad a usar",
      applicationUnit: "Unidad de aplicación",
      preparationProcedure: "Procedimiento de preparación",
      status: "Estado",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar la receta {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalRecipe: {
      addTitle: "Agregar Receta",
      updateTitle: "Actualizar Receta",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Recipes;
